import React from "react";
import { Zap, Droplets, Wind, Cable, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Card, Button } from "@/components/ui";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${color}22` }}><Icon size={20} color={color} /></div>
      <div>
        <div className="text-xl font-black">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

export default function MepReport() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();

  const { data, loading, error } = useDeliverable(() => api.editorMEP({ model: wm.model }), [wm?.title], !!wm?.model);
  const m = data?.metrics || {};
  const [exporting, setExporting] = React.useState(false);

  const exportBoq = async () => {
    setExporting(true);
    try {
      await api.editorMepBoq({ model: wm.model, floors: wm.floors || 1, project_name: wm.title || "Plan" });
      toast.success(isAr ? "تم تنزيل جدول كميات MEP (PDF)" : "MEP BOQ PDF downloaded");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <PageHeader eyebrow={isAr ? "MEP" : "MEP"} title={isAr ? "تقرير الكهرباء والسباكة والتكييف" : "MEP report"} subtitle={isAr ? "شبكة مترابطة: تغذية مياه من العداد، عمود صرف وخط صرف رئيسي بميل نحو المجاري، وتهوية سطح." : "Connected network: water main from the meter, a soil stack and a sloped building drain to the sewer, plus a roof vent."} />
          <DeliverableState loading={loading} error={error} isAr={isAr}>
            {data && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat icon={Zap} color="#FFD166" label={isAr ? "نقاط كهربائية" : "Electrical points"} value={m.electrical_count ?? 0} />
                  <Stat icon={Droplets} color="#0EA5E9" label={isAr ? "نقاط سباكة" : "Plumbing points"} value={m.plumbing_count ?? 0} />
                  <Stat icon={Wind} color="#2DD4BF" label={isAr ? "وحدات تكييف" : "HVAC units"} value={m.hvac_count ?? 0} />
                  <Stat icon={Cable} color="#A78BFA" label={isAr ? "أسلاك (م)" : "Wire (m)"} value={Math.round(m.total_wire_m ?? 0)} />
                  <Stat icon={Zap} color="#F59E0B" label={isAr ? "الحمل المقدّر (kW)" : "Est. load (kW)"} value={m.estimated_load_kw ?? 0} />
                  <Stat icon={Droplets} color="#38BDF8" label={isAr ? "إجمالي المواسير (م)" : "Total pipe (m)"} value={Math.round(m.total_pipe_m ?? 0)} />
                </div>

                <h3 className="mb-2 mt-6 text-sm font-black">{isAr ? "شبكة المياه والصرف (قابلة للتنفيذ)" : "Water & drainage network (buildable)"}</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat icon={Droplets} color="#2563EB" label={isAr ? "ماء بارد (م)" : "Cold water (m)"} value={Math.round(m.cold_water_m ?? 0)} />
                  <Stat icon={Droplets} color="#DC2626" label={isAr ? "ماء ساخن (م)" : "Hot water (m)"} value={Math.round(m.hot_water_m ?? 0)} />
                  <Stat icon={Droplets} color="#15803D" label={isAr ? "صرف (م)" : "Drainage (m)"} value={Math.round(m.drainage_m ?? 0)} />
                  <Stat icon={Droplets} color="#0F766E" label={isAr ? "قطر الخط الرئيسي" : "Main drain"} value={`Ø${m.main_drain_dia_mm ?? 0} mm`} />
                  <Stat icon={Droplets} color="#7C3AED" label={isAr ? "وحدات تصريف DFU" : "Drain units (DFU)"} value={m.drain_fixture_units ?? 0} />
                  <Stat icon={Wind} color="#0891B2" label={isAr ? "ميل الصرف" : "Drain slope"} value={`${m.drain_slope_pct ?? 1}%`} />
                </div>
                <p className="mt-4 rounded-lg border border-border bg-card/50 p-3 text-xs text-muted-foreground">
                  {isAr
                    ? `الشبكة تبدأ من عداد المياه عند الواجهة الأمامية وتغذّي كل نقطة رطبة، بينما تتجمّع كل نقاط الصرف في عمود صرف رئيسي (Ø110) ثم خط مبنى Ø${m.main_drain_dia_mm ?? 110} بميل ${m.drain_slope_pct ?? 1}% نحو ربط المجاري البلدي، مع تهوية سطح Ø75.`
                    : `The network starts at a front water meter feeding every wet point, while all drains collect into a soil stack (Ø110) then an Ø${m.main_drain_dia_mm ?? 110} building drain sloped ${m.drain_slope_pct ?? 1}% to the municipal sewer, with a Ø75 roof vent.`}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <Button variant="accent" loading={exporting} onClick={exportBoq} data-testid="mep-boq-export-btn">
                    <FileDown size={16} /> {isAr ? "تصدير جدول كميات MEP (PDF)" : "Export MEP BOQ (PDF)"}
                  </Button>
                  <span className="text-[11px] text-muted-foreground">
                    {isAr ? "جدول كميات للمقاول: أطوال المواسير والكابلات حسب القطر + الدوائر والأحمال." : "Contractor take-off: pipe & cable lengths by diameter + circuits and loads."}
                  </span>
                </div>
              </>
            )}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
